import moment from "moment";
import { Box } from "@analyticsMD/quil";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { blue, common, green, grey, orange, red } from "@mui/material/colors";
import { DANGER_BG, WARNING_BG, WARNING_TEXT } from "./malnutrition-colors";

import type { FC } from "react";
import type { MalnutritionStatus } from "../../types/Malnutrition";

interface StatusChipTheme {
  label: string;
  bg: string;
  fg: string;
  border: string;
  // Separate from `fg`: the designer gives the warning/success chips a darker
  // label than their icon for contrast, so the icon has its own colour.
  iconColor: string;
  icon: "error" | "check";
}

const STATUS_CHIP: Record<MalnutritionStatus, StatusChipTheme> = {
  high_risk: {
    label: "High Risk",
    bg: DANGER_BG,
    fg: red[800],
    border: red[200],
    iconColor: red[800],
    icon: "error",
  },
  medium_risk: {
    label: "Medium Risk",
    bg: WARNING_BG,
    fg: WARNING_TEXT,
    border: orange[200],
    iconColor: orange[900],
    icon: "error",
  },
  protocol: {
    label: "Protocol",
    bg: blue[50],
    fg: blue[900],
    border: blue[200],
    iconColor: blue[900],
    icon: "error",
  },
  following: {
    label: "Following",
    bg: common.white,
    fg: grey[700],
    border: grey[400],
    iconColor: grey[700],
    icon: "check",
  },
  consult: {
    label: "Consult",
    bg: green[50],
    fg: green[900],
    border: green[200],
    iconColor: green[800],
    icon: "check",
  },
};

const sxStyles = {
  row: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.5rem",
    // No bottom padding: the section below (feedback card / "following"
    // spacer) brings its own top spacing.
    padding: "0.75rem 1rem 0",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.375rem",
    boxSizing: "border-box",
    padding: "0.25rem 0.75rem",
    borderRadius: "999px",
    border: "1px solid",
    fontWeight: 700,
    fontSize: "0.875rem",
    lineHeight: 1.4,
    whiteSpace: "nowrap",
  },
  dischargeChip: {
    background: common.white,
    color: grey[900],
    borderColor: grey[400],
  },
  icon: { fontSize: "1.125rem" },
};

// The chip covers "discharging soon" only — today or tomorrow (ENG-30846). A
// date further out (or already past) isn't news worth a chip, so it collapses,
// as does a patient with no EDD in PE at all.
const dischargeChipLabel = (edd: string | null): string | null => {
  if (!edd) return null;
  // Explicit format so the date lands on local midnight rather than being read
  // as UTC (which shifts the day west of GMT), and strict so only the exact
  // shape the API promises is accepted. Loose parsing would quietly coerce a
  // partial value — "2026-08" becomes the 1st of the month — and chip a date
  // the patient doesn't have. (Overflowed dates like "2026-02-30" moment
  // rejects either way.)
  const eddDate = moment(edd, "YYYY-MM-DD", true);
  if (!eddDate.isValid()) return null;
  const daysOut = eddDate
    .clone()
    .startOf("day")
    .diff(moment().startOf("day"), "days");
  if (daysOut === 0) return "Discharge today";
  // Tomorrow shows the date rather than the word, per the design.
  if (daysOut === 1) return `Discharge ${eddDate.format("M/D/YY")}`;
  return null;
};

interface MalnutritionChipRowProps {
  // Null when the patient isn't flagged (or the status can't be mapped) — the
  // row then carries the discharge chip alone, or nothing at all.
  status: MalnutritionStatus | null;
  edd: string | null;
}

const MalnutritionChipRow: FC<MalnutritionChipRowProps> = ({ status, edd }) => {
  const statusTheme = status ? STATUS_CHIP[status] : null;
  const dischargeLabel = dischargeChipLabel(edd);

  if (!statusTheme && !dischargeLabel) return null;

  const StatusIcon =
    statusTheme?.icon === "check" ? CheckCircleIcon : ErrorIcon;

  return (
    <Box sx={sxStyles.row}>
      {statusTheme && (
        <Box
          role="status"
          aria-label={`Malnutrition status: ${statusTheme.label}`}
          sx={{
            ...sxStyles.chip,
            background: statusTheme.bg,
            color: statusTheme.fg,
            borderColor: statusTheme.border,
          }}
        >
          <StatusIcon sx={{ ...sxStyles.icon, color: statusTheme.iconColor }} />
          <span>{statusTheme.label}</span>
        </Box>
      )}
      {dischargeLabel && (
        <Box sx={{ ...sxStyles.chip, ...sxStyles.dischargeChip }}>
          <span>{dischargeLabel}</span>
        </Box>
      )}
    </Box>
  );
};

export default MalnutritionChipRow;
